import { useCallback } from 'react';
import { notification } from 'antd';
import type { VcsApi } from '@env0/vcs-service/api';
import { Popup } from 'utils/popup.utils';
import { intl } from 'components/localization';
import { useGetAuthorizationUrl } from 'hooks/use-get-authorization-url';
import type { OAuthResult } from 'components/organizations/vcs/vcs-connection-modal/vcs-connection-modal-form-oauth-authorization-button';

type UseVcsOAuthPopupProps = {
  connectionType: VcsApi.ConnectionType;
  vcsType: VcsApi.VcsType;
  accessScope?: string;
  onSuccess: (result: OAuthResult) => void;
  onError?: (error: string) => void;
};

export const useVcsOAuthPopup = ({
  connectionType,
  vcsType,
  accessScope,
  onSuccess,
  onError
}: UseVcsOAuthPopupProps) => {
  const authorizationUrl = useGetAuthorizationUrl({ connectionType, vcsType });

  const openOAuthPopup = useCallback(() => {
    Popup.open({
      url: authorizationUrl,
      closeByMessage: 'authorizationCallback',
      onBlock: () => {
        const errorMsg = intl.formatMessage({ id: 'vcs.authorize-button.blocked' });
        notification.error({
          message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
          description: errorMsg
        });
        onError?.(errorMsg);
      },
      onClosed: result => {
        if (!result?.accessScope) {
          const errorMsg = result?.error || intl.formatMessage({ id: 'vcs.authorize-button.window-closed' });
          notification.error({
            message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
        } else if (accessScope && accessScope !== result.accessScope) {
          const errorMsg = intl.formatMessage(
            {
              id: 'organization.settings.agents.vcs-connections-modal.connection.oauth.accessScopeWrong'
            },
            { accessScope }
          );
          notification.error({
            message: intl.formatMessage({ id: 'vcs.authorize-button.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
        } else {
          notification.success({
            message: intl.formatMessage({ id: 'vcs.authorize-button.success.title' }),
            description: intl.formatMessage({ id: 'vcs.authorize-button.success.description' })
          });
          onSuccess(result as OAuthResult);
        }
      },
      features: { height: 700, width: 700 }
    });
  }, [authorizationUrl, accessScope, onSuccess, onError]);

  return { openOAuthPopup };
};
